import { __decorate } from 'tslib';
import { unsafeCSS, css, LitElement, html } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';
import css_248z from './modal.styles.css.js';
import clsx from 'clsx';

var ModalSizeEnum;
(function (ModalSizeEnum) {
    ModalSizeEnum["fitContent"] = "fitContent";
    ModalSizeEnum["small"] = "small";
    ModalSizeEnum["big"] = "big";
    ModalSizeEnum["extraLarge"] = "extraLarge";
    ModalSizeEnum["fullHeight"] = "fullHeight";
})(ModalSizeEnum || (ModalSizeEnum = {}));
var ModalThemeEnum;
(function (ModalThemeEnum) {
    ModalThemeEnum["normal"] = "normal";
    ModalThemeEnum["lighter"] = "lighter";
})(ModalThemeEnum || (ModalThemeEnum = {}));
/**
 * Modal Component.
 *
 * @element ht-modal
 * @fires ht-modal-open - Fired when the modal opens
 * @fires ht-modal-close - Fired when the modal closes
 * @slot - The content of the modal
 * @slot header - Optional slot for custom header content
 */
let HTModal = class HTModal extends LitElement {
    constructor() {
        super(...arguments);
        this.class = '';
        this.classContent = '';
        this.backdropClose = true;
        this.open = false;
        this.height = 'fitContent';
        this.placeOn = null;
        this.size = 'fitContent';
        this.theme = 'normal';
        this.showBackdrop = true;
        this.title = '';
        this.blockScrollWhileOpen = false;
    }
    _handleSlotFooterChange({ target, }) {
        var _a;
        const hasFooter = target.assignedNodes().length > 0;
        (_a = this.modalFooter) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasFooter);
    }
    handleDialogClick(e) {
        if (this.backdropClose && e.target === this.dialog) {
            this.hide();
        }
    }
    firstUpdated() {
        if (this.open) {
            this.dialog.showModal();
        }
    }
    /**
     * Shows the modal
     */
    show() {
        if (!this.open) {
            this.open = true;
            this.dialog.showModal();
            this.dispatchEvent(new CustomEvent('ht-modal-open', {
                bubbles: true,
                composed: true,
            }));
        }
    }
    /**
     * Hides the modal
     */
    hide() {
        if (this.open) {
            this.dialog.close();
            this.dispatchEvent(new CustomEvent('ht-modal-close', {
                bubbles: true,
                composed: true,
            }));
        }
    }
    handleDialogClose() {
        this.open = false;
    }
    get wrapperClasses() {
        return clsx({
            'modal --fade-in-animation': true,
            [this.className]: !!this.className,
            '--theme-normal': this.theme === 'normal' || !this.theme,
            '--theme-lighter': this.theme === 'lighter',
            '--visible': this.open,
            '--with-backdrop': this.showBackdrop,
            '--width-size-small': this.size === ModalSizeEnum.small,
            '--width-size-big': this.size === ModalSizeEnum.big || !this.size,
            '--width-size-fit-content': this.size === ModalSizeEnum.fitContent,
            '--width-size-xxl': this.size === ModalSizeEnum.extraLarge,
            '--height-size-small': this.height === ModalSizeEnum.small,
            '--height-size-big': this.height === ModalSizeEnum.big || !this.height,
            '--height-size-fit-content': this.height === ModalSizeEnum.fitContent,
            '--height-size-full-height': this.height === ModalSizeEnum.fullHeight,
            '--place-on-left': this.placeOn === 'left',
            '--place-on-right': this.placeOn === 'right',
            '--place-on-center': this.placeOn === 'center' || !this.placeOn,
        });
    }
    render() {
        return html `
      <dialog
        @click="${this.handleDialogClick}"
        class="${this.wrapperClasses}"
        @close="${this.handleDialogClose}"
      >
        <div class="modal__header">
          <slot name="header">
            <div class="modal__header__content">
              <div class="modal__header__title">
                <p>${this.title}</p>
              </div>
              <button class="modal__header__close" @click="${this.hide}">
                &times;
              </button>
            </div>
          </slot>
        </div>
        <div class="modal__content">
          <slot></slot>
        </div>
        <div class="modal__footer" hidden>
          <slot
            name="footer"
            @slotchange="${this._handleSlotFooterChange}"
          ></slot>
        </div>
      </dialog>
    `;
    }
};
HTModal.styles = css `
    ${unsafeCSS(css_248z)}
  `;
__decorate([
    property({ type: String })
], HTModal.prototype, "class", void 0);
__decorate([
    property({ type: String })
], HTModal.prototype, "classContent", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HTModal.prototype, "backdropClose", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HTModal.prototype, "open", void 0);
__decorate([
    property({ type: String })
], HTModal.prototype, "height", void 0);
__decorate([
    property({ type: String })
], HTModal.prototype, "placeOn", void 0);
__decorate([
    property({ type: String })
], HTModal.prototype, "size", void 0);
__decorate([
    property({ type: String })
], HTModal.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], HTModal.prototype, "showBackdrop", void 0);
__decorate([
    query('dialog')
], HTModal.prototype, "dialog", void 0);
__decorate([
    query('.modal__footer')
], HTModal.prototype, "modalFooter", void 0);
__decorate([
    property({ type: String })
], HTModal.prototype, "title", void 0);
__decorate([
    property({ type: Boolean })
], HTModal.prototype, "blockScrollWhileOpen", void 0);
HTModal = __decorate([
    customElement('ht-modal')
], HTModal);
var HTModal$1 = HTModal;

export { ModalSizeEnum, ModalThemeEnum, HTModal$1 as default };
//# sourceMappingURL=modal.js.map
